import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import EyeSenseAnimation from "./EyeSenseAnimation";
import { FaShieldAlt, FaBrain, FaBell, FaCar, FaUserShield, FaFileAlt, FaDownload } from "react-icons/fa";


export default function EyeSense() {
  // State management
  const [dailyLogs, setDailyLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [visitorEntry, setVisitorEntry] = useState({
    name: "",
    purpose: "",
    timeIn: "",
    timeOut: "",
    gate: "Main Gate",
    suspicious: false,
  });
  const [filterType, setFilterType] = useState("all");
  const [showReportModal, setShowReportModal] = useState(false);

  // Load data from localStorage on mount
  useEffect(() => {
    const savedLogs = localStorage.getItem("eyeSenseLogs");
    const savedAlerts = localStorage.getItem("eyeSenseAlerts");
    if (savedLogs) setDailyLogs(JSON.parse(savedLogs));
    if (savedAlerts) setAlerts(JSON.parse(savedAlerts));
  }, []);

  // Save data to localStorage
  useEffect(() => {
    localStorage.setItem("eyeSenseLogs", JSON.stringify(dailyLogs));
    localStorage.setItem("eyeSenseAlerts", JSON.stringify(alerts));
  }, [dailyLogs, alerts]);

  // Simulate AI detection
  useEffect(() => {
    const interval = setInterval(() => {
      const suspiciousActivities = [
        "Unusual loitering detected near Main Gate",
        "Unauthorized access attempt at Service Gate",
        "Person running in restricted zone",
        "Face mismatch with registered database",
        "Object left unattended",
        "Multiple failed entry attempts",
        "Suspicious vehicle circling area",
      ];

      if (Math.random() < 0.2) {
        const randomActivity =
          suspiciousActivities[
          Math.floor(Math.random() * suspiciousActivities.length)
          ];
        const newAlert = {
          id: Date.now(),
          message: randomActivity,
          timestamp: new Date().toLocaleTimeString(),
          date: new Date().toLocaleDateString(),
          severity: "high",
          resolved: false,
        };
        setAlerts((prev) => [newAlert, ...prev].slice(0, 50));
      }
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setVisitorEntry((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleManualEntry = (e) => {
    e.preventDefault();
    if (!visitorEntry.name || !visitorEntry.purpose || !visitorEntry.timeIn) {
      alert("Please fill in all required fields");
      return;
    }

    const newLog = {
      id: Date.now(),
      ...visitorEntry,
      timestamp: new Date().toLocaleTimeString(),
      date: new Date().toLocaleDateString(),
      timeOut: visitorEntry.timeOut || "Not checked out",
    };

    setDailyLogs((prev) => [newLog, ...prev]);

    if (visitorEntry.suspicious) {
      const newAlert = {
        id: Date.now(),
        message: `Suspicious entry: ${visitorEntry.name} - ${visitorEntry.purpose}`,
        timestamp: new Date().toLocaleTimeString(),
        date: new Date().toLocaleDateString(),
        severity: "medium",
        resolved: false,
      };
      setAlerts((prev) => [newAlert, ...prev]);
    }

    setVisitorEntry({
      name: "",
      purpose: "",
      timeIn: "",
      timeOut: "",
      gate: "Main Gate",
      suspicious: false,
    });
  };

  const resolveAlert = (alertId) => {
    setAlerts((prev) =>
      prev.map((alert) =>
        alert.id === alertId ? { ...alert, resolved: true } : alert,
      ),
    );
  };

  const exportReport = () => {
    const reportData = {
      generatedAt: new Date().toISOString(),
      totalEntries: dailyLogs.length,
      suspiciousCount: dailyLogs.filter((log) => log.suspicious).length,
      regularCount: dailyLogs.filter((log) => !log.suspicious).length,
      unresolvedAlerts: alerts.filter((a) => !a.resolved).length,
      totalAlerts: alerts.length,
      logs: dailyLogs,
      alerts: alerts,
    };

    const dataStr = JSON.stringify(reportData, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute(
      "download",
      `EyeSense_Report_${new Date().toISOString().slice(0, 19)}.json`,
    );
    linkElement.click();
  };

  // Download Brochure/Software function
  const downloadEyeSenseBrochure = () => {
    const brochureData = {
      softwareName: "EyeSense AI Surveillance System",
      version: "2.0.0",
      releaseDate: new Date().toISOString(),
      features: [
        "Real-Time Threat Detection",
        "AI Behavior Analysis",
        "Instant Alerts & Notifications",
        "Automatic License Plate Recognition",
        "Face Recognition System",
        "Automated Report Generation",
        "24/7 Automated Monitoring",
        "Multi-Camera Support (50+ cameras)",
        "Cloud Backup & Storage",
        "Mobile App Integration",
      ],
      specifications: {
        uptime: "99.9%",
        accuracy: "95%",
        cameraSupport: "50+",
        storage: "Cloud-based",
        responseTime: "< 2 seconds",
      },
      pricing: {
        basic: "Free Trial Available",
        professional: "Contact Sales",
        enterprise: "Custom Quote",
      },
      contactInfo: {
        email: "sales@entelipro.com",
        phone: "+1 (555) 123-4567",
        website: "https://entelipro.com",
      },
    };

    const dataStr = JSON.stringify(brochureData, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute(
      "download",
      `EyeSense_AI_Brochure_${new Date().toISOString().slice(0, 10)}.json`,
    );
    linkElement.click();
  };
  const downloadBrochure = downloadEyeSenseBrochure;

  const visitEyeSense = () => {
    window.open("https://eyesense.indsac.com/", "_blank", "noopener,noreferrer");
  };

  const filteredLogs = dailyLogs.filter((log) => {
    if (filterType === "suspicious") return log.suspicious;
    if (filterType === "regular") return !log.suspicious;
    return true;
  });

  const unresolvedAlertsCount = alerts.filter((a) => !a.resolved).length;

  const eyeSenseSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "EyeSense AI Surveillance System",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Web, Windows, Linux, macOS",
    "description": "AI-powered surveillance system for real-time threat detection, behavior analysis, and automated monitoring.",
    "url": "https://www.entelipro.com/eyesense",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free Trial Available"
    }
  };

  return (
    <>
      <SEO
        title="EyeSense AI - Smart Surveillance & Threat Detection"
        description="EyeSense AI by EnteliPro is an intelligent surveillance system that detects suspicious activity, recognizes faces, and delivers real-time security alerts."
        keywords="EyeSense AI, AI Surveillance System, Threat Detection Software, Smart CCTV AI, Facial Recognition Security, License Plate Recognition"
        canonical="https://www.entelipro.com/eyesense"
        ogImage="https://www.entelipro.com/static/media/eyesense.png"
        schema={eyeSenseSchema}
      />

      <Navbar />

      {/* Breadcrumbs */}
      <section className="w3l-inner-banner-main">
        <div className="about-inner inner2">
          <div className="container seen-w3">
            <ul className="breadcrumbs-custom-path">
              <li>
                <Link to="/">
                  Home{" "}
                  <span className="fa fa-angle-right" aria-hidden="true"></span>
                </Link>
              </li>
              <li className="active">EyeSense AI Surveillance</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Download Button Section - Hero Area */}
      <section className="w3l-download-section">
        <div className="container">
          <div
            className="download-banner"
            style={{
              backgroundColor: "#f8f9fa",
              padding: "20px",
              borderRadius: "10px",
              marginBottom: "30px",
              textAlign: "center",
            }}
          >
            <h3>Experience EyeSense AI Today!</h3>
            <p style={{ marginBottom: "15px" }}>
              Visit the EyeSense web application and start protecting your premises with advanced AI
              surveillance
            </p>
            <div>
              <button
                onClick={visitEyeSense}
                className="btn btn-primary"
                style={{
                  marginRight: "10px",
                  padding: "10px 25px",
                  fontSize: "16px",
                  transition: "all 0.3s ease",
                }}
              >
                <i className="fa fa-external-link-alt" style={{ marginRight: "6px" }}></i> Visit EyeSense
              </button>
              <button
                onClick={downloadBrochure}
                className="btn btn-secondary"
                style={{ padding: "10px 25px", fontSize: "16px" }}
              >
                📄 Download Brochure
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="w3l-content-with-photo-4">
        <div className="content-with-photo4-block">
          <div className="container">
            <div className="cwp4-two row">
              <div className="col-xl-6 cwp4-image">
                <EyeSenseAnimation />
              </div>
              <div className="cwp4-text col-xl-6">
                <style>{`
                  .eyesense-intro-section h4 {
                    font-size: 28px;
                    font-weight: 800;
                    color: #1e293b;
                    letter-spacing: -0.5px;
                    margin-bottom: 10px;
                  }
                  .eyesense-intro-desc {
                    font-size: 15px;
                    line-height: 1.7;
                    color: #475569;
                    margin-bottom: 6px;
                  }
                  .eyesense-highlight-pills {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    margin: 16px 0 20px 0;
                  }
                  .eyesense-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(123,85,255,0.09);
                    border: 1px solid rgba(123,85,255,0.25);
                    color: #7b55ff;
                    font-size: 12.5px;
                    font-weight: 600;
                    padding: 5px 14px;
                    border-radius: 50px;
                    animation: pillFadeIn 0.5s ease both;
                    transition: all 0.25s ease;
                  }
                  .eyesense-pill:hover {
                    background: #7b55ff;
                    color: #fff;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(123,85,255,0.28);
                  }
                  .eyesense-pill:nth-child(1) { animation-delay: 0.0s; }
                  .eyesense-pill:nth-child(2) { animation-delay: 0.08s; }
                  .eyesense-pill:nth-child(3) { animation-delay: 0.16s; }
                  .eyesense-pill:nth-child(4) { animation-delay: 0.24s; }
                  @keyframes pillFadeIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                  }
                  .eyesense-stat-bars {
                    display: flex;
                    flex-direction: column;
                    gap: 14px;
                    margin-top: 22px;
                  }
                  .eyesense-stat-row {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                  }
                  .eyesense-stat-label-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 12.5px;
                    font-weight: 600;
                    color: #334155;
                  }
                  .eyesense-stat-pct {
                    color: #7b55ff;
                    font-weight: 700;
                  }
                  .eyesense-stat-track {
                    width: 100%;
                    height: 7px;
                    background: #e2e8f0;
                    border-radius: 20px;
                    overflow: hidden;
                  }
                  .eyesense-stat-fill {
                    height: 100%;
                    border-radius: 20px;
                    background: linear-gradient(90deg, #7b55ff, #38bdf8);
                    animation: fillBar 1.4s cubic-bezier(0.4,0,0.2,1) both;
                    box-shadow: 0 0 8px rgba(123,85,255,0.35);
                  }
                  @keyframes fillBar {
                    from { width: 0%; }
                  }
                  .eyesense-stat-fill.delay1 { animation-delay: 0.15s; }
                  .eyesense-stat-fill.delay2 { animation-delay: 0.35s; }
                  .eyesense-stat-fill.delay3 { animation-delay: 0.55s; }
                `}</style>
                <div className="eyesense-intro-section">
                  <h4>EyeSense AI – Intelligent Surveillance System</h4>
                  <p className="eyesense-intro-desc">
                    EyeSense AI is an advanced surveillance solution powered by
                    artificial intelligence that not only monitors but understands
                    human behavior in real-time. It detects suspicious activities,
                    analyzes patterns, and alerts you instantly before any threat
                    escalates.
                  </p>
                  <p className="eyesense-intro-desc">
                    Unlike traditional CCTV systems, EyeSense is proactive. It
                    eliminates blind spots, reduces dependency on manual
                    monitoring, and ensures faster response through AI-driven
                    alerts and insights.
                  </p>

                  {/* Animated Feature Pills */}
                  <div className="eyesense-highlight-pills">
                    <span className="eyesense-pill">🛡️ Real-Time Detection</span>
                    <span className="eyesense-pill">🧠 AI Behavior Analysis</span>
                    <span className="eyesense-pill">🔔 Instant Alerts</span>
                    <span className="eyesense-pill">🚗 License Plate AI</span>
                  </div>

                  {/* Animated Accuracy Bars */}
                  <div className="eyesense-stat-bars">
                    <div className="eyesense-stat-row">
                      <div className="eyesense-stat-label-row">
                        <span>Threat Detection Accuracy</span>
                        <span className="eyesense-stat-pct">99.9%</span>
                      </div>
                      <div className="eyesense-stat-track">
                        <div className="eyesense-stat-fill" style={{ width: "99.9%" }}></div>
                      </div>
                    </div>
                    <div className="eyesense-stat-row">
                      <div className="eyesense-stat-label-row">
                        <span>Face Recognition Match Rate</span>
                        <span className="eyesense-stat-pct">97.5%</span>
                      </div>
                      <div className="eyesense-stat-track">
                        <div className="eyesense-stat-fill delay1" style={{ width: "97.5%" }}></div>
                      </div>
                    </div>
                    <div className="eyesense-stat-row">
                      <div className="eyesense-stat-label-row">
                        <span>System Uptime</span>
                        <span className="eyesense-stat-pct">99.9%</span>
                      </div>
                      <div className="eyesense-stat-track">
                        <div className="eyesense-stat-fill delay2" style={{ width: "99.9%" }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="eyesense-features-section">
        <style>{`
          .eyesense-features-section {
            padding: 70px 0;
            background-color: #ffffff;
          }
          .eyesense-features-header {
            text-align: center;
            margin-bottom: 50px;
          }
          .eyesense-features-header h3 {
            font-size: 32px;
            font-weight: 700;
            color: #1e293b;
            margin-bottom: 12px;
            letter-spacing: -0.5px;
          }
          .eyesense-features-header p {
            font-size: 16px;
            color: #64748b;
            max-width: 600px;
            margin: 0 auto;
          }
          .eyesense-feature-card {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            padding: 32px 28px;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
            transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
                        box-shadow 0.28s cubic-bezier(0.4, 0, 0.2, 1),
                        border-color 0.28s cubic-bezier(0.4, 0, 0.2, 1);
            cursor: pointer;
            position: relative;
            overflow: hidden;
          }
          .eyesense-feature-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 3px;
            background: linear-gradient(90deg, transparent, #7b55ff, #38bdf8, transparent);
            transition: left 0.5s ease;
          }
          .eyesense-feature-card:hover::before {
            left: 100%;
          }
          .eyesense-feature-card::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 3px;
            background: linear-gradient(90deg, #7b55ff, #38bdf8);
            opacity: 0;
            transition: opacity 0.28s ease;
          }
          .eyesense-feature-card:hover::after {
            opacity: 1;
          }
          .eyesense-feature-card:hover {
            transform: translateY(-6px);
            border-color: #7b55ff;
            box-shadow: 0 12px 28px rgba(123, 85, 255, 0.15);
          }
          .eyesense-icon-wrapper {
            width: 54px;
            height: 54px;
            border-radius: 12px;
            background: rgba(123, 85, 255, 0.08);
            color: #7b55ff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            margin-bottom: 22px;
            transition: background 0.28s ease, color 0.28s ease, box-shadow 0.28s ease;
            flex-shrink: 0;
          }
          .eyesense-feature-card:hover .eyesense-icon-wrapper {
            background: #7b55ff;
            color: #ffffff;
            box-shadow: 0 6px 16px rgba(123, 85, 255, 0.3);
          }
          .eyesense-feature-card h4 {
            font-size: 19px;
            font-weight: 700;
            color: #1e293b;
            margin-bottom: 12px;
            line-height: 1.35;
            transition: color 0.28s ease;
          }
          .eyesense-feature-card:hover h4 {
            color: #7b55ff;
          }
          .eyesense-feature-card p {
            font-size: 14.5px;
            line-height: 1.65;
            color: #64748b;
            margin: 0;
            flex-grow: 1;
          }
          .eyesense-download-btn-container {
            text-align: center;
            margin-top: 46px;
          }
          .eyesense-download-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            background: #7b55ff;
            color: #ffffff;
            border: none;
            padding: 14px 38px;
            font-size: 16px;
            font-weight: 600;
            border-radius: 50px;
            box-shadow: 0 4px 18px rgba(123, 85, 255, 0.25);
            cursor: pointer;
            transition: all 0.28s ease;
            text-decoration: none;
          }
          .eyesense-download-btn:hover {
            background: #673ee6;
            color: #ffffff;
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(123, 85, 255, 0.38);
          }
        `}</style>
        <div className="container">
          <div className="eyesense-features-header">
            <h3>Advanced Features of EyeSense AI</h3>
            <p>
              Cutting-edge technology for comprehensive security surveillance
            </p>
          </div>
          <div className="row g-4">
            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <div className="eyesense-feature-card">
                <div className="eyesense-icon-wrapper">
                  <FaShieldAlt />
                </div>
                <h4>Real-Time Threat Detection</h4>
                <p>
                  Detect suspicious movements and unusual behavior instantly
                  using AI-powered computer vision with 99.9% accuracy.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <div className="eyesense-feature-card">
                <div className="eyesense-icon-wrapper">
                  <FaBrain />
                </div>
                <h4>AI Behavior Analysis</h4>
                <p>
                  EyeSense learns patterns and identifies anomalies to reduce
                  false alarms and improve accuracy over time through machine
                  learning.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <div className="eyesense-feature-card">
                <div className="eyesense-icon-wrapper">
                  <FaBell />
                </div>
                <h4>Instant Alerts & Notifications</h4>
                <p>
                  Get real-time alerts on your mobile or desktop the moment a
                  threat is detected with multi-channel notification system.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <div className="eyesense-feature-card">
                <div className="eyesense-icon-wrapper">
                  <FaCar />
                </div>
                <h4>Automatic License Plate Recognition</h4>
                <p>
                  Capture and log vehicle numbers automatically at all gates
                  for complete vehicle tracking and blacklist alerting.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <div className="eyesense-feature-card">
                <div className="eyesense-icon-wrapper">
                  <FaUserShield />
                </div>
                <h4>Face Recognition System</h4>
                <p>
                  Identify registered personnel, visitors, and blacklisted
                  individuals with high-precision facial recognition
                  technology.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <div className="eyesense-feature-card">
                <div className="eyesense-icon-wrapper">
                  <FaFileAlt />
                </div>
                <h4>Automated Report Generation</h4>
                <p>
                  Generate comprehensive daily, weekly, and monthly reports
                  for admin review with export options in multiple formats.
                </p>
              </div>
            </div>
          </div>

          {/* Centered Download Button below cards */}
          <div className="eyesense-download-btn-container">
            <button
              onClick={downloadEyeSenseBrochure}
              className="eyesense-download-btn"
            >
              <FaDownload size={18} /> Download EyeSense AI Now
            </button>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="w3l-content-with-photo-4">
        <div className="content-with-photo4-block">
          <div className="container">
            <div className="cwp4-two row">
              <div className="cwp4-text col-xl-12">
                <h4 className="text-center">
                  Why Choose EyeSense AI for Your Security Needs?
                </h4>
                <p className="para text-center">
                  EyeSense AI reduces security costs by 40% while increasing
                  threat detection accuracy by 95%. With 24/7 automated
                  monitoring, your security team can focus on what matters most
                  - rapid response to genuine threats rather than manual log
                  keeping and constant screen watching.
                </p>
                <div className="row mt-5">
                  <div className="col-md-4">
                    <div className="text-center">
                      <h5>✓ 99.9% Uptime</h5>
                      <p className="para">Reliable 24/7 surveillance</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-center">
                      <h5>✓ 50+ Cameras Support</h5>
                      <p className="para">Scalable for any facility size</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-center">
                      <h5>✓ Cloud Backup</h5>
                      <p className="para">Secure data storage & access</p>
                    </div>
                  </div>
                </div>
                {/* Visit EyeSense CTA */}
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "40px",
                    padding: "30px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "10px",
                  }}
                >
                  <h4>Ready to enhance your security?</h4>
                  <button
                    onClick={visitEyeSense}
                    style={{
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      padding: "15px 50px",
                      fontSize: "20px",
                      borderRadius: "5px",
                      cursor: "pointer",
                      marginTop: "15px",
                      fontWeight: "bold",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <i className="fa fa-external-link-alt" style={{ marginRight: "8px" }}></i> VISIT EYESENSE
                  </button>
                  <p style={{ marginTop: "15px", fontSize: "14px" }}>
                    Access EyeSense AI from any browser | No installation required
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
